import styled from 'styled-components'
import * as R from 'ramda'
import BaseIcon from 'sidecar-ui/lib/Icon'
import color from 'sidecar-theme/lib/color'
import contentLevels from 'sidecar-utils/lib/contentLevels';

export const Wrap = styled.div`
  position: absolute;
  left: 1rem;
  top: 1.8rem;
  z-index: ${contentLevels.components};
`

export const Wrapper = styled.div`
  position: relative;
`

export const IconLabel = styled.label`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  z-index: ${contentLevels.components + 3};
  right: 15px;
  height: 100%;
`

export const ValidationIcon = styled(BaseIcon)`
  font-size: 2.4rem;
  height: 2.4rem !important; 
  text-align: center;
  height: 100%;
  color: ${p => p.theme.iconColor};

  &.sidecon-checkmark {
    color: ${color.AQUAMARINE};
  }
`

export const StyledIcon = styled(BaseIcon)`
  font-size: 2.4rem;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  text-align: center;
  height: 100%;
  color: ${p => p.theme.iconColor};

  // the input is going to be at level 50 and the label is going to be at , so we're going
  // to be at level 52 to make the icons at +3 to show above them.
  z-index: ${contentLevels.components + 3};

  ${p => p.before && `left: 15px;`}
  ${p => p.after && `right: 15px;`}
  &.sidecon-checkmark {
    color: ${color.AQUAMARINE};
  }
`

// 'rtl' here is actually for inputs that need their text
// placed on the right side, not intended for Arabic or
// other true RTL language support.
export const Input = styled.input`
  position: relative;
  width: 100%;
  height: ${p => p.slim ? '5rem' : '6rem'};
  font-size: 1.8rem;
  vertical-align: middle;
  background: white;
  border: 1px solid ${p => p.theme.borderColor};
  transition: all .2s;
  padding: 0 2rem;
  font-family: apercu-regular-pro;
  color: ${color.MORTAR};

  // inputs should be at level so that we can make their
  // labels, icons, lists, etc all display above other inputs.
  z-index: ${contentLevels.components};

  ${p => p.disabled && `
    color: ${color.GREY};
    border-color: ${color.STONE};
    cursor: not-allowed;

    // for iphone safari, the contrast is too low
    -webkit-text-fill-color: ${color.GREY};
    opacity: 1;
  `}

  ${p => p.rtl && `text-align: right;`}

  // if we have a beforeIcon, we need more space on the left
  ${p => p.beforeIcon && `padding-left: 5rem;`}

  // if this field is being validated, put space on the right for icon
  ${p => p.validator && `padding-right: 4rem;`}

  &::placeholder {
    color: ${p => p.placeholder && !p.labelAsPlaceholder ? color.SILVER : 'transparent'};
    font-size: 1.8rem;
    transition: color .2s;
    transition-delay: .2s;

    // turns transparent when visited so that if the label goes back over
    // the placeholder, you can't see the placeholder peeking from behind.
    ${p => p.visited && `color: transparent;`}
  }

  // removes the 'spinner' (up/down) buttons on number fields
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    -moz-appearance: textfield;
    appearance: none;
  }

  // the above doesn't remove the up down arrows 
  // in firefox but this will
  &[type=number] {
    -moz-appearance: textfield;
  }

  &:hover {
    ${p => !p.disabled && `
      border-color: ${p => p.theme.hoverBorderColor};
    `}

    &:focus {
      border-color: ${p => p.theme.focusBorderColor}
    }
  }

  &:focus {
    border: 2px solid ${p => p.theme.focusBorderColor};
    
    // because the focus border is 1px thicker, we need to
    // adjust the padding to keep the text from shifting inside.
    padding-left: 1.9rem;
    ${p => p.beforeIcon && `padding-left: 4.9rem;`}

    &::placeholder {
      color: ${color.SILVER};
    }
  }

  &:active {
    &::placeholder {
      color: ${color.SILVER};
    }
  }

  // this is to make sure that the input
  // enough space below it to fit the entire sublabel
  ${p => p.subLabel && `margin-bottom: 1rem;`}
`

export const Label = styled.label`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0.8rem;
  left: 0.6rem;
  padding: 1.5rem;
  font-size: 1.8rem;
  transition: all 0.2s;
  color: ${p => p.theme.labelColor};

  // the +1 is to put the label on top of any input it goes with
  z-index: ${contentLevels.components+2};

  &:focus {
    padding-left: 1.9rem;
    border: 2px solid ${p => p.theme.focusBorderColor};
  }

  ${p => (p.clear || p.wide) && 'background: transparent;'}
  ${p => (p.wide) && `
    padding-top: .5rem;
    text-align: center;
  `}

  ${p => (p.active || p.dirty || !R.isEmpty(p.initial)) &&  `
    top: -1.1rem;
    left: 1rem;
    height: 2.2rem;
    padding: 0 0.5rem;
    font-size: 1.6rem;
  `};

  ${p => p.considerIcon && `
    left: 3rem;
  `}

  ${Input}:-webkit-autofill + & {
    top: -1.1rem;
    left: 1rem;
    height: 2.2rem;
    padding: 0 0.5rem;
    font-size: 1.6rem;
  }

  ${Input}:disabled + & {
    color: ${color.SILVER};
  }

  // using an :after element to block out a label sized portion of
  // the input's top border. We don't use a full label background
  // because we need the top half of the label to be transparent
  // for any color of background it's used on.
  &:after {
    content: "";
    display: block;
    background: #fff;
    height: 2px;
    position: absolute;
    top: 1.1rem;
    left: 0;
    right: 0;
    z-index: -1;
  }
`



// WEBPACK FOOTER //
// ./src/sidecar-ui/lib/InputText/styles.js